/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Stack,
  Typography,
  Alert,
  AlertTitle,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Controller, useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { getSubmitButtonProps, useApiClient } from '@marageti/z4-lib';
import {
  TripMatchStatus,
  TripMatchStatusUpdate,
} from '@marageti/z4-sdk/lib/travel';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';

const REFUND_OPTIONS = [
  {
    value: 'without-refund' as const,
    label: 'Without Commission Refund',
    helper:
      'This cancels the sold trip match without impacting sales performance. The Zicasso commission will be invoiced as usual.',
    status: TripMatchStatus.MatchSoldCanceled,
  },
  {
    value: 'with-refund' as const,
    label: 'With Commission Refund',
    helper:
      'This cancels the sold trip match and negatively impacts partner performance. Zicasso will refund the commission paid either fully or partially.',
    status: TripMatchStatus.MatchRefundedCanceled,
  },
];

const schema = z.object({
  refundType: z.enum(['without-refund', 'with-refund']),
  comments: z.string().min(1, { message: 'Please add a comment' }),
});

type CancelSoldTripFormData = z.infer<typeof schema>;

type CancelSoldTripModalProps = {
  open: boolean;
  onClose: () => void;
  tripMatchId: string;
  onSuccess?: () => void;
};

const CancelSoldTripModal = ({
  open,
  onClose,
  tripMatchId,
  onSuccess,
}: CancelSoldTripModalProps) => {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [submissionError, setSubmissionError] = useState<ApiError | null>(null);

  const {
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
    control,
    register,
    reset: resetForm,
  } = useForm<CancelSoldTripFormData>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: {
      refundType: 'without-refund',
      comments: '',
    },
  });

  const handleClose = () => {
    onClose();
    setSubmissionError(null);
    resetForm();
  };

  const onSubmit: SubmitHandler<CancelSoldTripFormData> = async (data) => {
    const option = REFUND_OPTIONS.find((opt) => opt.value === data.refundType)!;
    const statusUpdate: TripMatchStatusUpdate = {
      id: tripMatchId,
      status: option.status,
      statusInfo: {
        reason: `${option.label}: ${data.comments}`,
      },
    };

    try {
      await apiClient.tripMatchesClient.putTripMatchStatus(
        tripMatchId,
        statusUpdate,
      );
      enqueueSnackbar('Sold trip canceled.', { variant: 'success' });
      if (onSuccess) onSuccess();
      handleClose();
    } catch (e) {
      const apiError = e as ApiError;
      if (apiError?.code === 401) {
        navigate('/logout');
        return;
      }
      setSubmissionError(apiError);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!isSubmitting) handleClose();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ px: 4, pb: 0.5 }}>Cancel Sold Trip</DialogTitle>
      <DialogContent sx={{ px: 4, pb: 4 }}>
        <Typography color="primary.light" sx={{ mb: 3 }}>
          Please select the refund type and provide a reason for the
          cancellation. This action cannot be undone.
        </Typography>
        {submissionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <AlertTitle>There was an error on our end.</AlertTitle>
            {`Code: ${submissionError.code}: ${submissionError.message}`}
            {submissionError.id && (
              <>
                <br />
                {`ID: ${submissionError.id}`}
              </>
            )}
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={3}>
            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                px: 2,
                py: 1,
              }}
            >
              <Controller
                name="refundType"
                control={control}
                render={({ field }) => (
                  <RadioGroup {...field}>
                    {REFUND_OPTIONS.map((opt) => (
                      <FormControlLabel
                        key={opt.value}
                        value={opt.value}
                        control={
                          <Radio sx={{ alignSelf: 'flex-start', pt: 1 }} />
                        }
                        sx={{ alignItems: 'flex-start', my: 1, mr: 0 }}
                        label={(
                          <Box sx={{ py: 0.75 }}>
                            <Typography fontWeight="bold">
                              {opt.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {opt.helper}
                            </Typography>
                          </Box>
                        )}
                      />
                    ))}
                  </RadioGroup>
                )}
              />
            </Box>
            <TextField
              label="Comments"
              required
              placeholder="Describe the reason for canceling this trip"
              multiline
              rows={5}
              error={!!errors.comments}
              helperText={
                errors.comments?.message
                || 'These comments will be visible to the partner.'
              }
              {...register('comments')}
            />
            <Stack direction="row" spacing={2}>
              <LoadingButton
                type="submit"
                color="error"
                variant="contained"
                {...getSubmitButtonProps(isSubmitting, isValid)}
              >
                Confirm
              </LoadingButton>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CancelSoldTripModal;
